import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../utils/songUtils';
import SongListItem from './SongListItem';
import { tagIcons } from '../constants/tagConfig';

const SectionContainer = styled.div`
  margin: 50px 0;
  width: 100%;
  box-sizing: border-box;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: translateY(${props => props.$visible ? '0' : '20px'});
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;

  @media (min-width: 769px) and (max-width: 1280px) {
    padding: 0 15px;
  }

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin-bottom: 30px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    font-size: 1.2em;
  }

  @media (max-width: 768px) {
    text-align: center;
    justify-content: center;
  }
`;

const SongsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const LoadingContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #2E2E2E;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #ff4444;
  font-size: 16px;
`;

function CategorySongsSection({ category, visible = true }) {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
  const IconComponent = tagIcons[capitalizedCategory];

  useEffect(() => {
    const fetchSongs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE_URL}/songs`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const allSongs = await response.json();
        
        // Filter songs by category and sort (featured first, then by date)
        const filteredSongs = allSongs
          .filter(song => song.song_tags.includes(capitalizedCategory))
          .sort((a, b) => {
            // Featured songs come first
            if (a.song_featured && !b.song_featured) return -1;
            if (!a.song_featured && b.song_featured) return 1;
            // Then sort by date
            return new Date(b.date_added) - new Date(a.date_added);
          })
          .slice(0, 25); // Get only top 25 songs
        
        setSongs(filteredSongs);
      } catch (error) {
        console.error('Error fetching songs:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchSongs();
    }
  }, [category, capitalizedCategory]);

  if (loading) {
    return (
      <SectionContainer $visible={visible}>
        <SectionTitle>
          {IconComponent && <IconComponent />}
          {capitalizedCategory}
        </SectionTitle>
        <LoadingContainer>
          Loading tracks...
        </LoadingContainer>
      </SectionContainer>
    );
  }

  if (error) {
    return (
      <SectionContainer $visible={visible}>
        <SectionTitle>
          {IconComponent && <IconComponent />}
          {capitalizedCategory}
        </SectionTitle>
        <ErrorContainer>
          Error loading tracks. Please try again later.
        </ErrorContainer>
      </SectionContainer>
    );
  }

  if (songs.length === 0) {
    return (
      <SectionContainer $visible={visible}>
        <SectionTitle>
          {IconComponent && <IconComponent />}
          {capitalizedCategory}
        </SectionTitle>
        <LoadingContainer>
          No tracks available in this category at the moment.
        </LoadingContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer $visible={visible}>
      <SectionTitle>
        {IconComponent && <IconComponent />}
        {capitalizedCategory}
      </SectionTitle>
      <SongsList>
        {songs.map((song) => (
          <SongListItem 
            key={song.song_id} 
            song={song}
            shouldTrackPlay={false}
            isTagCategory={true}
            currentCategory={category}
          />
        ))}
      </SongsList>
    </SectionContainer>
  );
}

export default CategorySongsSection; 